import { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, getDocs, onSnapshot } from 'firebase/firestore';

const AuthContext = createContext(null);

const getSlugFromDomain = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost') return 'dev';
  if (hostname.includes('web.app') || hostname.includes('firebaseapp.com')) return 'dev';
  const parts = hostname.split('.');
  if (parts.length >= 4) return parts[0];
  return 'dev';
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const slug = getSlugFromDomain();

  const fetchProfile = async (firebaseUser) => {
    try {
      console.log('fetchProfile called for:', firebaseUser.uid, firebaseUser.email);

      const tokenResult = await firebaseUser.getIdTokenResult(true);
      const claims = tokenResult.claims;
      console.log('claims:', JSON.stringify(claims));

      let profile = null;
      let orgData = null;

      // Step 1 — try claims
      if (claims.orgId) {
        console.log('trying claims orgId:', claims.orgId);
        const [userDoc, orgDoc] = await Promise.all([
          getDoc(doc(db, 'organisations', claims.orgId, 'users', firebaseUser.uid)),
          getDoc(doc(db, 'organisations', claims.orgId)),
        ]);
        if (userDoc.exists()) profile = { id: firebaseUser.uid, ...userDoc.data() };
        if (orgDoc.exists()) orgData = { id: claims.orgId, ...orgDoc.data() };
        console.log('claims result — profile:', !!profile, 'org:', !!orgData);
      }

      // Step 2 — search by slug
      if (!profile) {
        console.log('searching by slug:', slug);
        const orgsSnap = await getDocs(collection(db, 'organisations'));
        console.log('total orgs:', orgsSnap.docs.length, orgsSnap.docs.map(d => d.id + '/' + d.data().slug));
        for (const orgDoc of orgsSnap.docs) {
          const data = orgDoc.data();
          if (slug && data.slug !== slug) {
            console.log('skipping org', orgDoc.id, 'slug mismatch', data.slug, '!=', slug);
            continue;
          }
          console.log('checking org', orgDoc.id, 'for uid', firebaseUser.uid);
          const userDoc = await getDoc(doc(db, 'organisations', orgDoc.id, 'users', firebaseUser.uid));
          console.log('user doc exists:', userDoc.exists());
          if (userDoc.exists()) {
            profile = { id: firebaseUser.uid, ...userDoc.data() };
            orgData = { id: orgDoc.id, ...data };
            break;
          }
        }
      }

      // Step 3 — brute force all orgs
      if (!profile) {
        console.log('brute forcing all orgs...');
        const orgsSnap = await getDocs(collection(db, 'organisations'));
        for (const orgDoc of orgsSnap.docs) {
          const userDoc = await getDoc(doc(db, 'organisations', orgDoc.id, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            profile = { id: firebaseUser.uid, ...userDoc.data() };
            orgData = { id: orgDoc.id, ...orgDoc.data() };
            console.log('found in org:', orgDoc.id);
            break;
          }
        }
      }

      console.log('FINAL profile:', JSON.stringify(profile));
      console.log('FINAL org:', JSON.stringify(orgData));

      setUserProfile(profile);
      setOrg(orgData);

      if (orgData?.name) document.title = orgData.name + ' · Agriware Ops';
      else document.title = 'Agriware Ops';

      if (orgData?.faviconURL) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'icon';
        link.href = orgData.faviconURL;
        document.head.appendChild(link);
      }

      if (orgData?.brandColour) {
        document.documentElement.style.setProperty('--color-brand', orgData.brandColour);
        document.documentElement.style.setProperty('--color-brand-light', orgData.brandColour + '18');
        document.documentElement.style.setProperty('--color-brand-dark', orgData.brandColour + 'cc');
      }

      return { profile, orgData };
    } catch (err) {
      console.error('fetchProfile ERROR:', err);
      setUserProfile(null);
      setOrg(null);
      return null;
    }
  };

  // The org is resolved once at sign-in, but its fields change while someone
  // is using the app — language, name, branding. Watching the document keeps
  // the interface current without a reload.
  useEffect(() => {
    // No signed-in user means no permission to read the org — the public
    // verify page mounts this provider too.
    if (!user || !org?.id) return;
    const unsub = onSnapshot(
      doc(db, 'organisations', org.id),
      (snap) => {
        if (snap.exists()) {
          setOrg((prev) => ({ ...prev, id: snap.id, ...snap.data() }));
        }
      },
      (err) => console.warn('org subscription error', err),
    );
    return unsub;
  }, [user, org?.id]);

  const refreshProfile = async () => {
    if (!user || !org) return;
    try {
      const userDoc = await getDoc(doc(db, 'organisations', org.id, 'users', user.uid));
      if (userDoc.exists()) {
        setUserProfile({ id: user.uid, ...userDoc.data() });
      }
    } catch (err) {
      console.error('refreshProfile error:', err);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('onAuthStateChanged:', firebaseUser?.uid || 'signed out');
      if (firebaseUser) {
        setUser(firebaseUser);
        await fetchProfile(firebaseUser);
      } else {
        setUser(null);
        setUserProfile(null);
        setOrg(null);
        document.title = 'Agriware Ops';
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const features = org?.features || {};

  const value = {
    user,
    userProfile,
    org,
    loading,
    role: userProfile?.role || null,
    orgId: org?.id || userProfile?.orgId || null,
    siteId: userProfile?.siteId || null,
    isAuthenticated: !!user,
    onboardingComplete: userProfile?.onboardingComplete || false,
    planStatus: org?.plan?.status || null,
    // Set by Agriware HQ; blocks access regardless of billing status.
    suspended: org?.plan?.suspended || null,
    features,
    hasFeature: (feature) => features[feature] === true,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}